import React, { useEffect, useState } from 'react';
import { decodeBase64 } from '../base64';
import { useParams } from 'react-router-dom';
import DocumentationForm from './DocumentationForm';
import FullDescriptionForm from './FullDescriptionForm';
import { displayEventDate } from '../dateUtils';
import Seo from './Seo';
import { createEventSeo, eventSlug, SITE_NAME, SITE_URL } from '../seo';

const owner = process.env.REACT_APP_GITHUB_OWNER;
const repo = process.env.REACT_APP_GITHUB_REPO;
const branch = process.env.REACT_APP_GITHUB_BRANCH || 'main';
const token = process.env.REACT_APP_GITHUB_TOKEN;
const eventsUrl = `https://raw.githubusercontent.com/${owner}/${repo}/${branch}/events.json`;

async function getFile(path) {
  const res = await fetch(`https://api.github.com/repos/${owner}/${repo}/contents/${path}?ref=${branch}`, {
    headers: token ? { Authorization: `token ${token}` } : {}
  });
  if (!res.ok) return null;
  return res.json();
}

async function updateFile(path, content, message, sha) {
  return fetch(`https://api.github.com/repos/${owner}/${repo}/contents/${path}`, {
    method: 'PUT',
    headers: {
      Authorization: `token ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ message, content, branch, sha })
  });
}

async function deleteFile(path, message, sha) {
  return fetch(`https://api.github.com/repos/${owner}/${repo}/contents/${path}`, {
    method: 'DELETE',
    headers: {
      Authorization: `token ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ message, sha, branch })
  });
}

function isSafeLinkUrl(url) {
  return /^(https?:\/\/|mailto:)/i.test(url);
}

function renderDescription(description) {
  const parts = [];
  const linkPattern = /\[([^\]]+)\]\(([^)\s]+)\)/g;
  let lastIndex = 0;
  let match;

  while ((match = linkPattern.exec(description)) !== null) {
    const [fullMatch, label, url] = match;
    if (!isSafeLinkUrl(url)) continue;

    if (match.index > lastIndex) {
      parts.push(description.slice(lastIndex, match.index));
    }

    parts.push(
      <a key={`${url}-${match.index}`} href={url} target="_blank" rel="noreferrer" className="underline">
        {label}
      </a>
    );
    lastIndex = match.index + fullMatch.length;
  }

  if (lastIndex < description.length) {
    parts.push(description.slice(lastIndex));
  }

  return parts.length ? parts : description;
}

function EventPage() {
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDocs, setShowDocs] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const loggedIn = localStorage.getItem('loggedIn') === 'true';

  useEffect(() => {
    async function loadEvent() {
      try {
        const apiRes = await fetch(
          `https://api.github.com/repos/${owner}/${repo}/contents/events.json?ref=${branch}`,
          { headers: token ? { Authorization: `token ${token}` } : {} }
        );
        let data = [];
        if (apiRes.ok) {
          const json = await apiRes.json();
          data = JSON.parse(decodeBase64(json.content));
        } else {
          const rawRes = await fetch(eventsUrl);
          if (rawRes.ok) {
            data = await rawRes.json();
          }
        }
        const found = (data || []).find((event) => eventSlug(event) === slug);
        if (found && found.draft && !loggedIn) {
          setEvent(null);
        } else {
          setEvent(found || null);
        }
      } catch {
        setEvent(null);
      } finally {
        setLoading(false);
      }
    }
    loadEvent();
  }, [slug]);


  if (loading) return <div className="p-10">Loading...</div>;
  if (!event) {
    return (
      <div className="p-10">
        <Seo
          title={`Event not found | ${SITE_NAME}`}
          description="This event page could not be found."
          canonicalUrl={`${SITE_URL}/events/${slug}/`}
          robots="noindex, nofollow"
        />
        Event not found
      </div>
    );
  }

  const displayDescription = event.fullDescription || event.description;
  const eventSeo = createEventSeo(event);

  return (
    <div className="pt-10 pb-40 mx-5 md:mx-[10vw]">
      <Seo {...eventSeo} />
      <div>
        <img src={event.imageUrl} alt={event.title} className="w-full h-auto mb-4" />
        <h1 className="text-3xl font-bold mb-2">{event.title}</h1>
        <div className="mb-4 text-gray-600">{displayEventDate(event.date, event.endDate)}</div>
        <p className="whitespace-pre-wrap leading-relaxed">{renderDescription(displayDescription)}</p>
        {loggedIn && (
          <div className="mt-4 flex flex-col items-start gap-2">
            <button className="underline" onClick={() => setShowFullDescription(true)}>
              Add Full Description
            </button>
            <button className="underline" onClick={() => setShowDocs(true)}>
              Add Documentation
            </button>
          </div>
        )}
        {[...Array(10)].map((_, idx) => {
          const num = String(idx + 1).padStart(2, '0');
          const url = event[`imgdoc${num}`];
          if (!url) return null;
          return (
            <div key={num} className="mt-4">
              <img src={url} alt={`documentation ${num}`} className="w-full h-auto" />
              {event[`imgdoc${num}Credit`] && (
                <div className="text-sm text-gray-600">{event[`imgdoc${num}Credit`]}</div>
              )}
            </div>
          );
        })}
      </div>
      {showFullDescription && (
        <FullDescriptionForm
          event={event}
          onClose={() => setShowFullDescription(false)}
          onSaved={(e) => setEvent(e)}
        />
      )}
      {showDocs && (
        <DocumentationForm
          event={event}
          onClose={() => setShowDocs(false)}
          onSaved={(e) => setEvent(e)}
        />
      )}
    </div>
  );
}

export default EventPage;
